import { ChangeEvent } from "react";

import { SelectEvent } from "@king-ict/eupisi-ui/components";

import MultipleChangeInformation from "./MultipleChangeInformation";
import { useAppDispatch } from "../../app/store";
import { updateInsertQuestionAnswer } from "../../slices/requestSlice";

const useInput = () => {
  const dispatch = useAppDispatch();

  const makeDeepCopy = <T>(obj: T): T => {
    return JSON.parse(JSON.stringify(obj));
  };

  const getIndexSuffix = (index: number) => {
    return `[${index}]`;
  };

  const getIndexedName = (index: number, name: string) => {
    return `${name}${getIndexSuffix(index)}`;
  };

  const resolveEventData = (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | SelectEvent
    >
  ) => {
    const { name } = event.target;
    let value;

    const inputEvent = event as ChangeEvent<HTMLInputElement>;
    if (inputEvent && inputEvent.target.type === "checkbox") {
      value = inputEvent.target.checked;
    } else if (inputEvent && inputEvent.target.type === "radio") {
      value = parseInt(event.target.value);
    } else {
      value = event.target.value;
    }

    return {
      name: name,
      value: value,
    };
  };

  const handleChange = (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | SelectEvent
    >,
    setState: (previousState: any) => void
  ) => {
    const { name, value } = resolveEventData(event);

    if (name) {
      setState((previousState: any) => ({
        ...previousState,
        [name]: value,
      }));
    }
  };

  const handleTextChange = (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | SelectEvent
    >,
    index: number,
    setState: (previousState: any) => void,
    questionId: number
  ) => {
    const { name, value } = resolveEventData(event);

    const answerText = event.target.value as string;

    const resolvedName =
      name && name.includes(getIndexSuffix(index))
        ? name.replace(getIndexSuffix(index), "")
        : name;

    if (resolvedName) {
      setState((previousState: any) => {
        // Create a deep copy of the previousState to prevent reference issues
        const stateCopy = makeDeepCopy(previousState);
        return stateCopy.map((prevItem: any, i: number) => {
          if (i === index) return { ...prevItem, [resolvedName]: value };
          return prevItem;
        });
      });
    }

    // Dispatch to Redux after the local state update
    if (questionId && answerText) {
      setTimeout(() => {
        dispatch(updateInsertQuestionAnswer({ questionId, answerText }));
      }, 0);
    }
  };

  const handleNumberChange = (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | SelectEvent
    >,
    index: number,
    setState: (previousState: any) => void,
    questionId: number
  ) => {
    const { name, value } = resolveEventData(event);

    const answerId = event.target.value as number;

    const resolvedName =
      name && name.includes(getIndexSuffix(index))
        ? name.replace(getIndexSuffix(index), "")
        : name;

    if (resolvedName) {
      setState((previousState: any) => {
        // Create a deep copy of the previousState to prevent reference issues
        const stateCopy = makeDeepCopy(previousState);
        return stateCopy.map((prevItem: any, i: number) => {
          if (i === index) return { ...prevItem, [resolvedName]: value };
          return prevItem;
        });
      });
    }

    // Dispatch to Redux after the local state update
    if (questionId && answerId) {
      setTimeout(() => {
        dispatch(updateInsertQuestionAnswer({ questionId, answerId }));
      }, 0);
    }
  };

  const handleNumberArrayChange = (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | SelectEvent
    >,
    index: number,
    setState: (previousState: any) => void,
    questionId: number
  ) => {
    const { name, value } = resolveEventData(event);

    const answerIds = event.target.value as number[];

    const resolvedName =
      name && name.includes(getIndexSuffix(index))
        ? name.replace(getIndexSuffix(index), "")
        : name;

    if (resolvedName) {
      const isAnswerIdName = resolvedName === "answerIds";

      // First update the local state
      setState((previousState: any) => {
        const stateCopy = makeDeepCopy(previousState);
        const temp = stateCopy.map((prevItem: any, i: number) => {
          if (i === index) {
            // For MultiSelect (when dealing with answerIds), we need to handle answerId field
            // explicitly to ensure consistency
            let updatedItem = {
              ...prevItem,
              [resolvedName]: value,
            };

            if (isAnswerIdName) {
              if (Array.isArray(value) && value.length) {
                // If we have values in the array, set answerId to null or undefined to avoid conflicts
                updatedItem.answerId = undefined;
              } else if (Array.isArray(value) && !value.length) {
                // If answerIds is empty, also clear answerId to prevent orphaned values
                updatedItem.answerId = undefined;
              }
            }

            return updatedItem;
          }
          return prevItem;
        });
        return temp;
      });
    }

    // Then dispatch to Redux after the local state update
    // This ensures the local state is updated before Redux tries to update global state
    if (!questionId) return;

    setTimeout(() => {
      // For MultiSelect questions, always provide both answerIds and set answerId to 0
      // to ensure the Redux state is consistent
      dispatch(
        updateInsertQuestionAnswer({
          questionId,
          answerIds,
          // Always explicitly set answerId to 0 when dealing with answerIds
          // to ensure no stray answerId values remain
          answerId: 0,
        })
      );
    }, 0);
  };

  const handleMultipleChange = (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | SelectEvent
    >,
    valueProp: string,
    collection: any[],
    changeInformations: MultipleChangeInformation[],
    setState: (previousState: any) => void
  ) => {
    const { value } = resolveEventData(event);
    const valueItem = collection.find(
      (c) => (c[valueProp] as string) === value
    );

    setState((previousState: any) => {
      // Create a deep copy of the previousState to prevent reference issues
      const stateCopy = makeDeepCopy(previousState);
      return {
        ...stateCopy,
        ...changeInformations.reduce(
          (previous, current) => ({
            ...previous,
            [current.stateProp]: valueItem
              ? valueItem[current.valueProp]
              : undefined,
          }),
          {}
        ),
      };
    });
  };

  return {
    getIndexedName,
    handleChange,
    handleTextChange,
    handleNumberChange,
    handleNumberArrayChange,
    handleMultipleChange,
  };
};

export default useInput;
